import { createContext, useContext } from 'react';

import { styled, capitalize, Box } from '@mui/material';
import clsx from 'clsx';

import { Sizes, SxProps, WithRef } from '../../types';

export type AvatarSize = Sizes<
  '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
>;

export type AvatarProps = {
  text: string;
  variant?: 'circular' | 'square';
  size?: AvatarSize;
  color?: 'blue' | 'green' | 'indigo' | 'aqua' | 'yellow' | 'red' | 'grey';
  className?: string;
  disabled?: boolean;
  testId?: string;
  sx?: SxProps;
};

const AvatarRoot = styled(
  ({
    ref,
    className,
    text,
    sx,
    testId,
  }: WithRef<HTMLDivElement, AvatarProps>) => {
    return (
      <Box ref={ref} className={clsx(className)} data-testid={testId} sx={sx}>
        {text.substring(0, 2)}
      </Box>
    );
  },
  {
    name: 'DsAvatar',
    slot: 'Root',
    target: 'DsAvatar-root',
    overridesResolver: ({ variant, size, color, disabled }, theme) => {
      return [
        theme.root,
        color ? theme[variant + capitalize(color.toString())] : {},
        theme[variant],
        theme[size],
        disabled ? theme.disabled : {},
      ];
    },
  }
)<AvatarProps>({});

AvatarRoot.displayName = 'AvatarRoot';

export const AvatarContext = createContext<Partial<AvatarProps> | undefined>(
  undefined
);

export const Avatar = ({
  ref,
  ...props
}: WithRef<HTMLDivElement, AvatarProps>) => {
  const avatarContext = useContext(AvatarContext) ?? {};
  return <AvatarRoot {...avatarContext} {...props} ref={ref} />;
};

Avatar.displayName = 'Avatar';
